import { type Product, type WebPage, type WithContext } from 'schema-dts'

import {
  type PimProductWithVariantsV1,
  type ProductReviewServiceReducedReviewV1,
} from '@redteclab/api/clients/bully'

import {
  productDataAccessorGetBrand,
  productDataAccessorGetManufacturer,
  productDataAccessorGetManufacturerCode,
} from '../../model/productDataAccessor'

import { getOfferSchema } from './getOfferSchema'
import { getAggregatedRatingSchema } from './getRatingSchema'
import { getReviewSchema } from './getReviewSchema'
import { getSimilarToSchema } from './getSimilarToSchema'
import { type PartiallyRequired } from './jsonLdSchema.types'

// we currently load only 5 reviews per request "page" (see productPageApiFetchPimProductReviews)
const NUMBER_OF_CONSIDERED_RATINGS = 5

const getReviewAverage = (
  reviews: ProductReviewServiceReducedReviewV1[],
): number => {
  if (reviews.length === 0) {
    return 0
  }

  const sum = reviews.reduce((prev, current) => prev + current.rating, 0)

  return sum / reviews.length
}

/**
 * Undefined values cut during serialization, but Google requires them,
 * so we fill keys with empty strings
 */
type GoogleProduct = PartiallyRequired<
  Product,
  'brand' | 'description' | 'image' | 'manufacturer' | 'mpn'
>

const getProductSchema = ({
  fallback,
  isMarketplace,
  product,
  reviews,
}: ProductPageJsonLdPageDescriptionData): GoogleProduct => {
  const consideredReviews = reviews.slice(0, NUMBER_OF_CONSIDERED_RATINGS)
  const reviewCount = product.rating?.count ?? NUMBER_OF_CONSIDERED_RATINGS
  const averageRating =
    product.rating?.average ?? getReviewAverage(consideredReviews)

  return {
    '@type': 'Product',
    aggregateRating: getAggregatedRatingSchema(
      Number(averageRating.toFixed(1)),
      reviewCount,
    ),
    brand: productDataAccessorGetBrand(product)?.name ?? '',
    description: product.description_short ?? product.description_extra ?? '',
    image: product.images?.at(0) ?? fallback,
    isSimilarTo: getSimilarToSchema(product, isMarketplace, fallback),
    manufacturer: productDataAccessorGetManufacturer(product) ?? '',
    mpn: productDataAccessorGetManufacturerCode(product) ?? '',
    name: product.name,
    offers: getOfferSchema(product, isMarketplace),
    review:
      consideredReviews.length > 0
        ? consideredReviews.map(getReviewSchema)
        : undefined,
    sku: product.upid,
  }
}

interface ProductPageJsonLdPageDescriptionData {
  fallback: string
  isMarketplace: boolean
  product: PimProductWithVariantsV1
  reviews: ProductReviewServiceReducedReviewV1[]
}

export const productPageGetJsonLdProductPageDescriptionSchema = (
  data: ProductPageJsonLdPageDescriptionData,
): WithContext<WebPage> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    mainEntity: getProductSchema(data),
  }
}
